import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useActiveTeam } from './useActiveTeam';
import { ChooseTeamScreen } from './ChooseTeamScreen';

/**
 * Routes that bypass the tenant gate entirely: the OAuth round-trip handlers
 * (a session may not exist yet) and the onboarding wizard (reached FROM the
 * gate to onboard a new team, so it must render without an active team).
 */
const BYPASS_PREFIXES = ['/global/auth/', '/onboarding'];

function isBypassPath(pathname: string): boolean {
  return BYPASS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

/**
 * Tenant entry gate. Composed INSIDE {@link AuthGate}, so its children only
 * render for a signed-in user on a protected route. It further requires an
 * active team to be chosen:
 *  - Bypass routes (auth callback, /onboarding) always render children.
 *  - When not authenticated, render children (AuthGate governs those routes;
 *    on public routes it passes through, on protected routes it shows sign-in).
 *  - When authenticated with no active team, show the "Choose your team" gate.
 *  - Otherwise render the app; every request is then tenant-scoped via
 *    buildMetaInfo reading the persisted active team.
 */
export function TenantGate({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const { activeTeam } = useActiveTeam();

  if (isBypassPath(location.pathname)) {
    return <>{children}</>;
  }
  if (status !== 'authenticated') {
    return <>{children}</>;
  }
  if (!activeTeam) {
    return <ChooseTeamScreen />;
  }
  return <>{children}</>;
}

export default TenantGate;
